import { useTranslation } from 'react-i18next';
import Badge from '@/components/base/Badge';
import DataTable, { type Column } from '@/components/base/DataTable';
import { formatCurrency } from '@/lib/format';
import type { ItemServiceRate, LaundryItem } from '@/types';
import { ITEM_SERVICE_TYPES } from '@/types';

interface ItemsTableProps {
  items: LaundryItem[];
  currency: string;
  busyId: string | null;
  onEdit: (item: LaundryItem) => void;
  onToggle: (item: LaundryItem) => void;
  onDelete: (item: LaundryItem) => void;
}

function ItemCell({ item }: { item: LaundryItem }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-200 bg-background-100">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name} className="h-full w-full object-cover" />
        ) : (
          <i className="ri-shirt-line text-xl text-foreground-400" aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-foreground-950">{item.name}</p>
        <p className="lb-ltr-field mt-0.5 text-xs text-foreground-400">{item.id}</p>
        <span className="sr-only">{t('items.table.item')}</span>
      </div>
    </div>
  );
}

function ServicePriceCell({ rate, currency }: { rate: ItemServiceRate; currency: string }) {
  const { t } = useTranslation();
  if (!rate.available) {
    return <span className="text-xs text-foreground-400">{t('items.price.unavailable')}</span>;
  }
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-foreground-400">{t('items.price.normal')}</span>
        <span className="lb-ltr-field text-sm font-semibold text-foreground-900">
          {formatCurrency(rate.normal ?? 0, currency)}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-foreground-400">{t('items.price.urgent')}</span>
        <span className="lb-ltr-field text-sm font-semibold text-accent-700">
          {formatCurrency(rate.urgent ?? 0, currency)}
        </span>
      </div>
    </div>
  );
}

function StatusCell({ active }: { active: boolean }) {
  const { t } = useTranslation();
  return (
    <Badge variant={active ? 'success' : 'neutral'} icon={active ? 'ri-checkbox-circle-line' : 'ri-eye-off-line'}>
      {t(active ? 'items.status.active' : 'items.status.inactive')}
    </Badge>
  );
}

interface RowActionsProps {
  item: LaundryItem;
  busy: boolean;
  onEdit: (item: LaundryItem) => void;
  onToggle: (item: LaundryItem) => void;
  onDelete: (item: LaundryItem) => void;
}

function RowActions({ item, busy, onEdit, onToggle, onDelete }: RowActionsProps) {
  const { t } = useTranslation();
  const base =
    'flex h-9 w-9 items-center justify-center rounded-md border transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50';
  return (
    <div className="flex items-center justify-end gap-1.5">
      <button
        type="button"
        title={t('items.action.edit')}
        aria-label={t('items.action.edit')}
        disabled={busy}
        onClick={() => onEdit(item)}
        className={`${base} border-background-300 text-foreground-700 hover:bg-background-100`}
      >
        <i className="ri-pencil-line text-base" aria-hidden="true" />
      </button>
      <button
        type="button"
        title={t(item.active ? 'items.action.deactivate' : 'items.action.activate')}
        aria-label={t(item.active ? 'items.action.deactivate' : 'items.action.activate')}
        disabled={busy}
        onClick={() => onToggle(item)}
        className={`${base} ${
          item.active
            ? 'border-amber-200 text-amber-700 hover:bg-amber-50'
            : 'border-secondary-200 bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
        }`}
      >
        <i className={item.active ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'} aria-hidden="true" />
      </button>
      <button
        type="button"
        title={t('items.action.delete')}
        aria-label={t('items.action.delete')}
        disabled={busy}
        onClick={() => onDelete(item)}
        className={`${base} border-rose-200 text-rose-600 hover:bg-rose-50`}
      >
        <i className="ri-delete-bin-line text-base" aria-hidden="true" />
      </button>
    </div>
  );
}

export default function ItemsTable({
  items,
  currency,
  busyId,
  onEdit,
  onToggle,
  onDelete,
}: ItemsTableProps) {
  const { t } = useTranslation();

  const columns: Column<LaundryItem>[] = [
    { key: 'item', header: t('items.table.item'), render: (item) => <ItemCell item={item} /> },
    ...ITEM_SERVICE_TYPES.map((key) => ({
      key,
      header: t(`items.service.${key}`),
      render: (item: LaundryItem) => <ServicePriceCell rate={item.services[key]} currency={currency} />,
    })),
    { key: 'status', header: t('items.table.status'), render: (item) => <StatusCell active={item.active} /> },
    {
      key: 'actions',
      header: t('items.table.actions'),
      className: 'text-end',
      render: (item) => (
        <RowActions
          item={item}
          busy={busyId === item.id}
          onEdit={onEdit}
          onToggle={onToggle}
          onDelete={onDelete}
        />
      ),
    },
  ];

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block">
        <DataTable columns={columns} rows={items} rowKey={(item) => item.id} />
      </div>

      {/* Mobile cards */}
      <ul className="divide-y divide-background-100 md:hidden">
        {items.map((item) => (
          <li key={item.id} className="p-4">
            <div className="flex items-start justify-between gap-3">
              <ItemCell item={item} />
              <StatusCell active={item.active} />
            </div>
            <div className="mt-3 space-y-2 rounded-md border border-background-200 bg-background-100/50 p-3">
              {ITEM_SERVICE_TYPES.map((key) => (
                <div key={key} className="flex items-start justify-between gap-3">
                  <span className="text-xs font-medium text-foreground-600">{t(`items.service.${key}`)}</span>
                  <ServicePriceCell rate={item.services[key]} currency={currency} />
                </div>
              ))}
            </div>
            <div className="mt-3 border-t border-background-100 pt-3">
              <RowActions
                item={item}
                busy={busyId === item.id}
                onEdit={onEdit}
                onToggle={onToggle}
                onDelete={onDelete}
              />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}